import { Clock, ControlSource, PlayState, ScrollEngineConfig, ScrollEngineState } from './types';
import { SystemClock } from './clock';

export type ChangeListener = (state: ScrollEngineState) => void;
export type EndListener = () => void;

export class ScrollEngine {
  private y = 0.0;
  private maxY = 0.0;
  private targetSpeed = 80.0;
  private currentSpeed = 0.0;
  private playState: PlayState = 'stopped';
  private controlSource: ControlSource = 'manual';
  private clampedFrameCount = 0;

  private lastTimeMs: number | null = null;
  private readonly dtMax: number;
  private readonly suspensionThreshold: number;
  private readonly rampDurationMs: number;
  private readonly clock: Clock;

  // External follow velocity override (from Voice Follow Controller)
  private voiceVelocity = 0.0;

  private changeListeners: Set<ChangeListener> = new Set();
  private endListeners: Set<EndListener> = new Set();

  constructor(config: ScrollEngineConfig = {}) {
    this.dtMax = config.dtMax ?? 100.0;
    this.suspensionThreshold = config.suspensionThreshold ?? 250.0;
    this.rampDurationMs = config.rampDurationMs ?? 200.0;
    this.clock = config.clock ?? new SystemClock();
  }

  getState(): ScrollEngineState {
    return {
      y: this.y,
      maxY: this.maxY,
      targetSpeed: this.targetSpeed,
      currentSpeed: this.currentSpeed,
      playState: this.playState,
      controlSource: this.controlSource,
      clampedFrameCount: this.clampedFrameCount,
    };
  }

  onChange(listener: ChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  onEnd(listener: EndListener): () => void {
    this.endListeners.add(listener);
    return () => this.endListeners.delete(listener);
  }

  private notifyChange(): void {
    const state = this.getState();
    for (const listener of this.changeListeners) {
      listener(state);
    }
  }

  private notifyEnd(): void {
    for (const listener of this.endListeners) {
      listener();
    }
  }

  /**
   * Sets content dimensions.
   * contentHeight includes preRoll and postRoll.
   */
  setContent(contentHeight: number, viewportHeight: number): void {
    this.maxY = Math.max(0, contentHeight - viewportHeight);
    if (this.y > this.maxY) {
      this.y = this.maxY;
      this.notifyChange();
    }
  }

  setSpeed(speedPxPerSec: number): void {
    this.targetSpeed = Math.max(2, Math.min(1500, speedPxPerSec));
    // If playing in manual mode, speed ramps to new target smoothly
    this.notifyChange();
  }

  setControlSource(source: ControlSource): void {
    if (this.controlSource !== source) {
      this.controlSource = source;
      if (source === 'voice') {
        this.currentSpeed = 0.0;
        this.voiceVelocity = 0.0;
      }
      this.notifyChange();
    }
  }

  setVoiceVelocity(vPxPerSec: number): void {
    this.voiceVelocity = vPxPerSec;
  }

  play(): void {
    if (this.playState === 'playing') return;
    this.playState = 'playing';
    this.lastTimeMs = null; // Will initialize on first tick
    this.notifyChange();
  }

  pause(): void {
    if (this.playState === 'paused') return;
    this.playState = 'paused';
    this.currentSpeed = 0.0;
    this.lastTimeMs = null;
    this.notifyChange();
  }

  stop(): void {
    this.playState = 'stopped';
    this.y = 0.0;
    this.currentSpeed = 0.0;
    this.voiceVelocity = 0.0;
    this.lastTimeMs = null;
    this.notifyChange();
  }

  reset(): void {
    this.stop();
  }

  seek(newY: number): void {
    const clamped = Math.max(0, Math.min(this.maxY, newY));
    if (clamped !== this.y) {
      this.y = clamped;
      this.notifyChange();
    }
  }

  /**
   * Applies user input delta (wheel, touch, arrow keys).
   * Instant response, autoplay resumes if currently playing.
   */
  applyUserDelta(dy: number): void {
    const prevY = this.y;
    this.y = Math.max(0, Math.min(this.maxY, this.y + dy));
    if (this.y !== prevY) {
      this.notifyChange();
    }
  }

  /**
   * Resets clock reference to prevent jumps after sleep, tab hide, or pause
   */
  resetClock(): void {
    this.lastTimeMs = null;
  }

  /**
   * Ticks the engine by one frame.
   * Can accept explicit timestamp or query injected clock.
   */
  tick(nowMs?: number): ScrollEngineState {
    const now = nowMs ?? this.clock.now();

    if (this.playState !== 'playing') {
      this.lastTimeMs = null;
      return this.getState();
    }

    if (this.lastTimeMs === null) {
      this.lastTimeMs = now;
      return this.getState();
    }

    const rawDelta = now - this.lastTimeMs;
    this.lastTimeMs = now;

    // Suspension check: raw gap > threshold (e.g. 250ms) treated as tab suspend
    if (rawDelta > this.suspensionThreshold) {
      this.clampedFrameCount++;
      return this.getState();
    }

    // Clamp dt to DT_MAX (e.g. 100ms)
    let dt = rawDelta;
    if (dt > this.dtMax) {
      this.clampedFrameCount++;
      dt = this.dtMax;
    }
    if (dt < 0) dt = 0;

    const dtSeconds = dt / 1000.0;

    // Calculate effective speed based on control mode
    if (this.controlSource === 'manual') {
      // Smoothly ramp toward targetSpeed
      const rampRate = (this.targetSpeed / this.rampDurationMs) * 1000.0; // px/s^2
      if (this.currentSpeed < this.targetSpeed) {
        this.currentSpeed = Math.min(this.targetSpeed, this.currentSpeed + rampRate * dtSeconds);
      } else if (this.currentSpeed > this.targetSpeed) {
        this.currentSpeed = Math.max(this.targetSpeed, this.currentSpeed - rampRate * dtSeconds);
      }
      this.y += this.currentSpeed * dtSeconds;
    } else {
      // Voice mode: follow controller sets voiceVelocity
      this.currentSpeed = this.voiceVelocity;
      this.y += this.voiceVelocity * dtSeconds;
    }

    // Boundary check
    if (this.maxY > 0 && this.y >= this.maxY) {
      this.y = this.maxY;
      this.pause();
      this.notifyEnd();
    } else if (this.y < 0) {
      this.y = 0;
    }

    this.notifyChange();
    return this.getState();
  }
}
